// @mui
import PropTypes from 'prop-types';
import { 
  Avatar, 
  Modal, 
  Box, 
  Stack, 
  Link, 
  Card, 
  Typography, 
  CardHeader,
  Pagination,
  IconButton
} from '@mui/material';
import { useState } from 'react';
// utils
import { fToNow } from '../../../utils/formatTime';
// components
import Iconify from '../../../components/iconify';

// ----------------------------------------------------------------------

OneMessageNews.propTypes = {
  title: PropTypes.string,
  subheader: PropTypes.string,
  list: PropTypes.array.isRequired,
};

export default function OneMessageNews({ title, subheader, list, ...other }) {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 1;
  const totalPages = Math.ceil(list.length / itemsPerPage);
  
  const currentIndex = (currentPage - 1) * itemsPerPage;
  const currentItem = list[currentIndex];

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <Card {...other}>
      <CardHeader 
        title={title} 
        subheader={subheader}
        action={
          totalPages > 1 && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <IconButton 
                onClick={handlePrevious} 
                disabled={currentPage === 1}
                size="small"
              >
                <Iconify icon="eva:arrow-left-fill" />
              </IconButton>
              <Typography variant="body2">
                {currentPage} з {totalPages}
              </Typography>
              <IconButton 
                onClick={handleNext} 
                disabled={currentPage === totalPages}
                size="small"
              >
                <Iconify icon="eva:arrow-right-fill" />
              </IconButton>
            </Stack>
          )
        }
      />
      
      {!list.length ? (
        <Typography variant="body2" sx={{ p: 3.5, pb: 0 }}>
          Ще немає повідомлень
        </Typography>
      ) : (
        <Box sx={{ p: 3 }}>
          {currentItem && <NewsItem news={currentItem} />}
          
          {totalPages > 1 && (
            <Stack alignItems="center" sx={{ mt: 3 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="small"
                showFirstButton
                showLastButton
              />
            </Stack>
          )}
        </Box>
      )}
    </Card>
  );
}

// ----------------------------------------------------------------------

NewsItem.propTypes = {
  news: PropTypes.shape({
    description: PropTypes.string,
    image: PropTypes.string,
    postedAt: PropTypes.string,
    title: PropTypes.string,
    link: PropTypes.string,
    lesson: PropTypes.shape({
      edmap: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      task: PropTypes.string,
      image: PropTypes.string,
    }),
  }),
};

function NewsItem({ news }) {
  const { link, description, postedAt, lesson } = news;
  const [openImage, setOpenImage] = useState(false);
  const [modalImage, setModalImage] = useState('');

  return (
    <Stack direction="row" alignItems="center" spacing={2}>

      <Modal
        open={openImage}
        onClose={() => setOpenImage(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Stack alignItems="center" justifyContent="center" sx={{ height: '100vh' }}>
          <Box
            sx={{
              maxWidth: '90%',
              maxHeight: '90%',
              bgcolor: 'background.paper',
              boxShadow: 24,
              borderRadius: 1,
              p: 2
            }}
          >
            <img 
              src={modalImage} 
              alt="modal lala" 
              style={{ 
                maxWidth: '100%', 
                maxHeight: '100%', 
                objectFit: 'contain' 
              }} 
            />
          </Box>
        </Stack>
      </Modal>

      <Box sx={{ minWidth: 240, flexGrow: 1 }}>

        <Stack direction="row" spacing={2}>
          {lesson ? (
            <Stack spacing={2}>
              <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                <div>
                  Edmap {lesson.edmap} {lesson.name}
                </div>
                {lesson.task}
              </Typography>
              <Avatar
                variant="square"
                sx={{ width: 150, height: 'auto', alignSelf: 'center' }}
                src={lesson.image}
                onClick={() => {
                  setOpenImage(true);
                  setModalImage(lesson.image);
                }}
                onKeyDown={() => {
                  setOpenImage(true);
                  setModalImage(lesson.image);
                }}
                style={{ cursor: 'pointer' }}
              />
            </Stack>
          ) : null}
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {description}
            {postedAt && (
              <Box component="span" sx={{ display: 'block', textAlign: 'right', fontSize: '0.75rem', mt: 0.5 }}>
                {fToNow(postedAt)}
              </Box>
            )}
          </Typography>
        </Stack>

        {link ? (
          <Link href={link} target="_blank">
            {link}
          </Link>
        ) : null}
      </Box>
    </Stack>
  );
}